/**
 * LanguageContext.tsx
 * مصدر الحقيقة الوحيد للغة الواجهة (عربي/إنجليزي) — مستقلّ عن weatherStore عمداً
 * لأن لوحة الأدمن حزمة منفصلة تماماً بلا وصول له. يُركَّب مرّتين (App.tsx وAdminApp.tsx)
 * وكلاهما يقرأ/يكتب نفس مفتاح localStorage فيبقى الاختيار متّسقاً بين الموقعين.
 *
 * تطبيق dir/lang على <html> يحدث هنا مرّة واحدة فقط — لا حاجة لـ dir="rtl" متفرّق
 * في أي مكوّن آخر؛ كل شيء يتوارثه من الجذر تلقائياً.
 *
 * أولوية اختيار اللغة: تخزين المستخدم ⇒ ?lang= في الرابط ⇒ كشف الموقع (detectLang).
 */
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { AR_DICTIONARY, type DictionaryKey } from './dictionaries/ar';
import { EN_DICTIONARY } from './dictionaries/en';
import { detectLang } from './detectLang';
import type { Lang } from './types';

export type { Lang };

const STORAGE_KEY = 'rw_lang';
const DICTIONARIES: Record<Lang, Record<string, string>> = {
    ar: AR_DICTIONARY,
    en: EN_DICTIONARY,
};

/** قيم الاستبدال داخل النص: t('settings.model.ago', { d: '3h' }) */
export type TParams = Record<string, string | number>;

function readInitialLang(): Lang {
    // مسار /en/ التزام صريح من الرابط نفسه — يفوز حتى على تفضيل محفوظ سابقاً. مطابق
    // للفحص المطابق في index.html (سكربت الإقلاع) — أي تعديل هنا يقابله تعديل هناك.
    try {
        if (window.location.pathname.replace(/\/+$/, '') === '/en') return 'en';
    } catch {
        // بيئة بلا window.location قابلة للقراءة — نتجاهل
    }
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored === 'ar' || stored === 'en') return stored;
    } catch {
        // localStorage غير متاح (وضع خاص/SSR) — نتجاهل ونكمل للكشف التلقائي
    }
    try {
        const fromUrl = new URLSearchParams(window.location.search).get('lang');
        if (fromUrl === 'ar' || fromUrl === 'en') return fromUrl;
    } catch {
        // بيئة بلا window.location قابلة للقراءة — نتجاهل
    }
    return detectLang();
}

function interpolate(template: string, params?: TParams): string {
    if (!params) return template;
    return template.replace(/\{(\w+)\}/g, (match, key) =>
        key in params ? String(params[key]) : match);
}

interface LanguageContextValue {
    lang: Lang;
    setLang: (lang: Lang) => void;
    toggleLang: () => void;
    /**
     * ترجمة مفتاح مسطّح "namespace.key". المفتاح المعروف مُتحقَّق منه وقت الترجمة؛
     * والمفاتيح المبنية ديناميكياً (مثل `layer.${id}`) تُمرَّر كنصّ ويعود المفتاح نفسه
     * إن غاب — فيظهر النقص فوراً في الواجهة بدل نصّ فارغ.
     */
    t: (key: DictionaryKey | (string & {}), params?: TParams) => string;
    dir: 'rtl' | 'ltr';
    /** وسم BCP-47 لواجهات Intl (تنسيق التواريخ والأرقام). */
    locale: 'ar' | 'en-US';
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
    const [lang, setLangState] = useState<Lang>(readInitialLang);

    useEffect(() => {
        document.documentElement.lang = lang;
        document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr';
    }, [lang]);

    const setLang = (next: Lang) => {
        setLangState(next);
        try {
            localStorage.setItem(STORAGE_KEY, next);
        } catch {
            // تجاهل فشل التخزين — التبديل يبقى يعمل لجلسة الصفحة الحالية
        }
    };

    const toggleLang = () => setLang(lang === 'ar' ? 'en' : 'ar');

    const value = useMemo<LanguageContextValue>(() => {
        const dict = DICTIONARIES[lang];
        return {
            lang,
            setLang,
            toggleLang,
            t: (key: string, params?: TParams) => interpolate(dict[key] ?? key, params),
            dir: lang === 'ar' ? 'rtl' : 'ltr',
            locale: lang === 'ar' ? 'ar' : 'en-US',
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lang]);

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useTranslation(): LanguageContextValue {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error('useTranslation must be used within LanguageProvider');
    return ctx;
}
